/**
 * EN + AR copy for the Price Brief (issue 552 redesign).
 *
 * The app has no runtime i18n framework yet — the established pattern (see
 * `web/src/pages/agent/reports/copy.ts`) is a per-page copy object keyed by
 * locale, read through a `t(key, locale, vars)` helper. Arabic strings are
 * provisional where a fuller translation pass is still pending.
 *
 * `{{var}}` placeholders are interpolated with Western numerals everywhere —
 * the design spec (§4) pins numerals to Western digits even in Arabic.
 */

export type PriceBriefLocale = 'en' | 'ar'

type Localized = { en: string; ar: string }

function L(en: string, ar: string): Localized {
  return { en, ar }
}

export const PRICE_BRIEF_COPY = {
  pageTitle: L('Price brief', 'موجز السعر'),

  // Subject strip
  effectiveSample: L('effective sample {{value}}', 'العينة الفعلية {{value}}'),
  confidenceLabel: L('Confidence', 'الثقة'),
  confidenceLow: L('LOW', 'منخفضة'),
  confidenceMedium: L('MEDIUM', 'متوسطة'),
  confidenceHigh: L('HIGH', 'مرتفعة'),
  asOf: L('Brief pulled {{date}}', 'سُحب الموجز في {{date}}'),
  fxFresh: L('FX rate fresh', 'سعر الصرف محدّث'),
  rateStale: L('Exchange rate is {{age}} old', 'سعر الصرف عمره {{age}}'),

  // The verdict
  verdictEyebrow: L('The verdict', 'الخلاصة'),
  shouldBePriced: L('Should be priced around', 'السعر المناسب تقريبًا'),
  priceRange: L('{{low}}–{{high}}', '{{low}}–{{high}}'),
  percentileReadout: L('{{ordinal}} percentile', 'النسبة المئوية {{ordinal}}'),
  readoutAbove: L(
    '{{pct}}% above the median of {{count}} comparable properties',
    'أعلى من الوسيط بـ{{pct}}% من أصل {{count}} عقار مشابه',
  ),
  readoutBelow: L(
    '{{pct}}% below the median of {{count}} comparable properties',
    'أقل من الوسيط بـ{{pct}}% من أصل {{count}} عقار مشابه',
  ),
  readoutAt: L(
    'Right at the median of {{count}} comparable properties',
    'عند الوسيط تمامًا من أصل {{count}} عقار مشابه',
  ),
  readoutNoPrice: L(
    'The middle of {{count}} comparable properties',
    'وسط {{count}} عقار مشابه',
  ),
  theirPrice: L('Their price', 'السعر المطلوب'),
  ladderLow: L('Low', 'الأدنى'),
  ladderP25: L('25th', '25%'),
  ladderMedian: L('Median', 'الوسيط'),
  ladderP75: L('75th', '75%'),
  ladderHigh: L('High', 'الأعلى'),
  priceLabel: L('Asking price', 'سعر الطلب'),
  pricePlaceholder: L('No price set', 'لم يُحدَّد سعر'),
  currencyUsd: L('USD', 'دولار'),
  currencyLbp: L('LBP', 'ليرة'),
  lbpNote: L(
    'LBP amounts convert at the session rate; its age is shown beside the verdict.',
    'تُحوَّل مبالغ الليرة بسعر جلسة الصرف، ويظهر عمره بجانب الخلاصة.',
  ),

  // The evidence
  evidenceEyebrow: L('The evidence', 'الأدلة'),
  evidenceCount: L('{{count}} comparables, weighted by resemblance', '{{count}} مقارنات موزونة حسب التشابه'),
  legendVerifiedSale: L('Verified sale', 'بيع موثّق'),
  legendInternal: L('On WingCaster', 'على WingCaster'),
  legendPortal: L('Portal asking price', 'سعر طلب من بوابة'),
  srEvidenceTableCaption: L('Comparables behind the dot strip', 'المقارنات وراء شريط النقاط'),
  srKind: L('Kind', 'النوع'),
  srPrice: L('Price', 'السعر'),
  srSource: L('Source', 'المصدر'),
  srSize: L('Size', 'المساحة'),
  srSimilarity: L('Resemblance', 'التشابه'),
  srObserved: L('Observed', 'الرصد'),
  kindVerifiedSale: L('Verified sale', 'بيع موثّق'),
  kindAsking: L('Asking price', 'سعر طلب'),
  observedSold: L('Sold {{date}}', 'بِيع في {{date}}'),
  observedFirstSeen: L('First seen {{date}}', 'شوهد أول مرة في {{date}}'),
  cardPerSqm: L('{{value}} per m²', '{{value}} للمتر المربع'),
  cardSize: L('{{value}} m²', '{{value}} م²'),
  cardBedsBaths: L('{{beds}} bd · {{baths}} ba', '{{beds}} غرف نوم · {{baths}} حمامات'),
  cardSimilarity: L('{{pct}}% resemblance', 'تشابه {{pct}}%'),
  cardDaysListed: L('{{days}} days since listed', '{{days}} يوم منذ العرض'),
  cardClose: L('Close evidence', 'إغلاق الأدلة'),

  // The area, honestly
  areaEyebrow: L('The area, honestly', 'المنطقة، بصراحة'),
  activeLabel: L('Active listings', 'قوائم نشطة'),
  activeBasis: L('active listings that we hold', 'قائمة نشطة نملك بياناتها'),
  daysLabel: L('Median time listed', 'الوسيط الزمني للعرض'),
  daysBasis: L('Median {{days}} days since we first saw them', 'الوسيط {{days}} يومًا منذ أن رأيناها أول مرة'),
  soldLabel: L('Verified sales', 'مبيعات موثّقة'),
  soldBasis: L('{{count}} verified sales, last {{window}} days', '{{count}} بيع موثّق خلال آخر {{window}} يومًا'),
  soldMedian: L('median {{value}}', 'وسيطها {{value}}'),

  // Pool quality
  poolEyebrow: L('Pool quality', 'جودة العينة'),
  poolStrong: L('Close resemblance', 'تشابه قريب'),
  poolModerate: L('Loose', 'متوسط'),
  poolWeak: L('Distant', 'بعيد'),
  poolMedianSimilarity: L('Median similarity', 'التشابه الوسيط'),
  poolPullLine: L('{{strong}} of {{total}} pull the answer', '{{strong}} من أصل {{total}} هي التي تحدّد الإجابة'),

  // Subject & criteria panel
  panelTitle: L('Subject & criteria', 'العقار والمعايير'),
  panelSelectors: L('Selectors — changing these re-runs the whole brief', 'المحددات — تغييرها يُعيد تشغيل الموجز كاملًا'),
  panelComparators: L('Comparators — refines the weighting, never the pool', 'المقارنات — تُحسّن التوزيع ولا تغيّر العينة'),
  fieldPropertyType: L('Property type', 'نوع العقار'),
  fieldTransactionType: L('Transaction', 'نوع الصفقة'),
  fieldTransactionSale: L('Sale', 'بيع'),
  fieldTransactionRent: L('Rent', 'إيجار'),
  typeApartment: L('Apartment', 'شقة'),
  typeVilla: L('Villa', 'فيلا'),
  typeTownhouse: L('Townhouse', 'تاون هاوس'),
  typeStudio: L('Studio', 'ستوديو'),
  typePenthouse: L('Penthouse', 'بنتهاوس'),
  typeOffice: L('Office', 'مكتب'),
  typeShop: L('Shop', 'محل'),
  fieldLocation: L('Location', 'الموقع'),
  fieldLocationPlaceholder: L('City, neighbourhood or place name', 'المدينة أو الحي أو اسم المكان'),
  fieldArea: L('Area (m²)', 'المساحة (م²)'),
  fieldBedrooms: L('Bedrooms', 'غرف النوم'),
  fieldBathrooms: L('Bathrooms', 'الحمامات'),
  historyLabel: L('Price history', 'سجل الأسعار'),
  historyEyebrow: L('How the market has moved', 'كيف تحرّك السوق'),
  benchmarkEyebrow: L('Asking against achieved', 'سعر الطلب مقابل المحقّق'),
  // Price history (graph 1). The window is stated, never implied: the
  // producer backfills only as far as the evidence reaches.
  historyTooShort: L(
    'A trend needs two closed quarters. This fills in as quarters close, and reaches further back as agents report older verified sales.',
    'يحتاج الاتجاه إلى ربعين مكتملين. يمتلئ هذا مع إغلاق الأرباع، ويعود أبعد مع إبلاغ الوكلاء عن مبيعات موثّقة أقدم.',
  ),
  historyChange: L(
    '{{change}}% across the {{window}} we hold evidence for ({{from}} to {{to}}).',
    '{{change}}% عبر {{window}} التي نملك عنها أدلة ({{from}} إلى {{to}}).',
  ),
  historyWindowYears: L('{{years}} years', '{{years}} سنوات'),
  historyWindowQuarters: L('{{quarters}} quarters', '{{quarters}} أرباع'),
  historyUp: L('up', 'ارتفاعًا'),
  historyDown: L('down', 'انخفاضًا'),
  historyAria: L(
    'Median sold price by quarter from {{from}} to {{to}}, {{direction}} {{change}} percent overall.',
    'الوسيط لسعر البيع حسب الربع من {{from}} إلى {{to}}، {{direction}} {{change}} بالمئة إجمالًا.',
  ),
  historyPointTitle: L(
    '{{quarter}}: {{price}} from {{count}} sold observations',
    '{{quarter}}: {{price}} من {{count}} ملاحظات بيع',
  ),
  historyBasis: L(
    'Median of verified sold prices per quarter — asking prices are never averaged in. A hollow, dashed point is a thin quarter: the line passes through it, but do not quote it alone.',
    'وسيط أسعار البيع الموثّقة لكل ربع — لا تُحتسب أسعار الطلب ضمنه أبدًا. النقطة المجوّفة المتقطعة تعني ربعًا ضعيف الأدلة: يمر الخط بها، لكن لا تستشهد بها وحدها.',
  ),

  // Asking vs achieved by quarter (graph 2).
  benchmarkLabel: L('Asking against achieved', 'سعر الطلب مقابل المحقّق'),
  benchmarkNoDates: L(
    'No comparable carries a usable date, so there is nothing to place on a timeline.',
    'لا يحمل أي عقار مقارن تاريخًا صالحًا، لذا لا يوجد ما يُوضع على خط زمني.',
  ),
  benchmarkGap: L(
    'In {{period}}, sold prices sat {{percent}}% {{direction}} asking — the discount this market is taking.',
    'في {{period}}، جاءت أسعار البيع {{percent}}% {{direction}} سعر الطلب — وهو الخصم الذي يأخذه هذا السوق.',
  ),
  benchmarkBelow: L('below', 'دون'),
  benchmarkAbove: L('above', 'فوق'),
  benchmarkNoGap: L(
    'No quarter yet holds enough of both asking and sold prices to compare them.',
    'لا يوجد ربع يحتوي بعد على ما يكفي من أسعار الطلب والبيع لمقارنتهما.',
  ),
  benchmarkBucketAria: L(
    '{{period}} {{kind}}: {{count}} observations, median {{median}}',
    '{{period}} {{kind}}: {{count}} ملاحظات، الوسيط {{median}}',
  ),
  benchmarkBasis: L(
    'Box is the middle half, bar the median, whisker the full range. Filled is a verified sale, hollow an asking price. Under {{min}} observations a quarter shows its points, not a box.',
    'الصندوق هو النصف الأوسط، والشريط هو الوسيط، والشعيرة هي المدى الكامل. المملوء بيع موثّق، والمجوّف سعر طلب. تحت {{min}} ملاحظات يعرض الربع نقاطه لا صندوقًا.',
  ),

  // Empty / loading / insufficient / error / upsell
  emptyTitle: L('Set the subject to run the brief', 'حدّد العقار لإنشاء الموجز'),
  emptyBody: L(
    'Pick a property type, a transaction and a location — the brief re-runs itself as you refine it.',
    'اختر نوع العقار ونوع الصفقة والموقع — وسيُعيد الموجز تشغيل نفسه مع كل تحديد.',
  ),
  loadingAria: L('Loading the price brief', 'جارٍ تحميل موجز السعر'),
  insufficientEyebrow: L('Not enough evidence', 'الأدلة غير كافية'),
  insufficientTitle: L('The engine refused to bound this price', 'تعذّر تحديد نطاق السعر'),
  insufficientIntro: L('What the engine said:', 'ما قاله المحرك:'),
  insufficientFixLocation: L(
    'What would fix it: add coordinates, or pick a named city or area.',
    'ما يصلح ذلك: أضف الإحداثيات أو اختر مدينة أو منطقة معروفة بالاسم.',
  ),
  insufficientFixPool: L(
    'What would fix it: pick a named area nearby, or a different property type — the engine refuses to widen the pool silently.',
    'ما يصلح ذلك: اختر منطقة معروفة قريبة أو نوع عقار آخر — فالمحرك يرفض توسيع العينة بصمت.',
  ),
  errorRateTitle: L('Exchange rate temporarily unavailable', 'سعر الصرف غير متاح مؤقتًا'),
  errorRateBody: L(
    'The brief needs a fresh USD conversion rate to bound the pool. Retry in a moment.',
    'يحتاج الموجز إلى سعر صرف محدّث بالدولار لتحديد النطاق. أعد المحاولة بعد لحظات.',
  ),
  errorGenericTitle: L('The brief could not run', 'تعذّر إنشاء الموجز'),
  errorRetry: L('Retry', 'إعادة المحاولة'),
  upsellTitle: L('This feature is part of the Pro package', 'هذه الميزة جزء من باقة Pro'),
  upsellBody: L(
    'Market analysis across the comparable pool is a Pro capability.',
    'تحليل السوق عبر عينة المقارنات ميزة من باقة Pro.',
  ),
  upsellCta: L('See the Pro package', 'اطّلع على باقة Pro'),
  fieldErrorFallback: L('This field needs attention', 'هذا الحقل يحتاج إلى مراجعة'),
} as const

export type PriceBriefCopyKey = keyof typeof PRICE_BRIEF_COPY

export function priceBriefT(
  key: PriceBriefCopyKey,
  locale: PriceBriefLocale,
  vars?: Record<string, string | number>,
): string {
  let text = PRICE_BRIEF_COPY[key][locale]
  if (vars) {
    for (const [name, value] of Object.entries(vars)) {
      text = text.split(`{{${name}}}`).join(String(value))
    }
  }
  return text
}

/** Property-type display names, keyed by the app's existing taxonomy. */
export const PROPERTY_TYPE_LABELS: Record<string, Localized> = {
  apartment: L('Apartment', 'شقة'),
  villa: L('Villa', 'فيلا'),
  townhouse: L('Townhouse', 'تاون هاوس'),
  studio: L('Studio', 'ستوديو'),
  penthouse: L('Penthouse', 'بنتهاوس'),
  office: L('Office', 'مكتب'),
  shop: L('Shop', 'محل'),
}

export function propertyTypeLabel(propertyType: string, locale: PriceBriefLocale): string {
  return PROPERTY_TYPE_LABELS[propertyType]?.[locale] ?? propertyType
}

/** "62nd percentile" / Western-digits fallback for Arabic. */
export function ordinalForm(value: number, locale: PriceBriefLocale): string {
  if (locale === 'ar') return String(value)
  const mod100 = value % 100
  if (mod100 >= 11 && mod100 <= 13) return `${value}th`
  switch (value % 10) {
    case 1:
      return `${value}st`
    case 2:
      return `${value}nd`
    case 3:
      return `${value}rd`
    default:
      return `${value}th`
  }
}

/** Observed dates print Western numerals and a stable '14 Mar 2026' shape. */
export function formatObservedDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date)
}
